import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { fetchExtensions } from '../../api/fetchExtensions/fetchExtensions';
import { IExtension, IExtensionsResponse } from '../../api/fetchExtensions/types';
import { IAggregator, IConfiguredAddon } from '../../types/aggregator.types';
import { getBrandId } from '../../helpers/getBrandId/getBrandId';

/**
 * The service-extensions response is a flat list of extension records (one per
 * brand+addon connection). The old channels response was already grouped by
 * external system, so reconstruct that grouping here to keep IAggregator[]
 * stable for every downstream consumer.
 *
 * An extension can carry more than one instance (e.g. two Oracle instances
 * pointing at different hierarchy units), so this yields one IConfiguredAddon
 * per instance rather than collapsing to a single one.
 */
const toConfiguredAddons = (extension: IExtension): IConfiguredAddon[] =>
  extension.instances.map((instance) => ({
    addonId: extension.addon.id,
    addonName: extension.addon.name,
    instanceId: instance.id,
    instanceName: instance.name,
    settings: instance.settings,
    endpoints: instance.endpoints.map((endpoint) => ({
      endpointId: endpoint.id,
      endpointName: endpoint.type.name,
      endpointCode: endpoint.type.code,
      endpointUrl: endpoint.override_url ?? endpoint.url ?? '',
      isEnabled: endpoint.is_enabled,
      lastUpdated: endpoint.last_updated,
    })),
  }));

const groupByExternalSystem = (extensions: IExtension[]): Map<string, IExtension[]> => {
  const groups = new Map<string, IExtension[]>();
  extensions.forEach((extension) => {
    // external_system can be null; such extensions can't be presented as a channel, so skip them.
    const externalSystemId = extension.addon.external_system?.id;
    if (!externalSystemId) return;
    const group = groups.get(externalSystemId);
    if (group) {
      group.push(extension);
    } else {
      groups.set(externalSystemId, [extension]);
    }
  });
  return groups;
};

export const useExternalSystems = (
  options?: Omit<
    UseQueryOptions<IExtensionsResponse, unknown, IAggregator[], string[]>,
    'queryKey' | 'queryFn' | 'select'
  >,
) => {
  return useQuery({
    queryKey: ['external-systems', getBrandId()],
    queryFn: fetchExtensions,
    staleTime: 60000, // 1 minute
    select: (response: IExtensionsResponse) => {
      const groups = groupByExternalSystem(response?.data || []);
      return Array.from(groups.entries()).map(([externalSystemId, extensions]): IAggregator => {
        const externalSystem = extensions[0].addon.external_system;
        const configuredAddons = extensions.flatMap(toConfiguredAddons);
        return {
          aggregatorId: externalSystemId,
          name: externalSystem?.name,
          description: externalSystem?.integration_type_name ?? undefined,
          integrationType: externalSystem?.integration_type_name ?? undefined,
          ...(configuredAddons.length === 1 && {
            extensionInstanceId: configuredAddons[0].instanceId,
            extensionInstanceName: configuredAddons[0].instanceName,
          }),
          configuredAddons,
        };
      });
    },
    ...options,
  });
};
